import { GetStaticPaths, GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import { useTranslation } from 'next-i18next';

import { CHANNEL } from '@boss/constants/b2b-b2c';
import { events as eventService, store as storeService } from '@boss/services';
import { Crumble, Environment } from '@boss/types/b2b-b2c';
import { getLocalizedSlugForEventPage, getTranslatedSlugsByType, slugify } from '@boss/utils';

import { ComponentMapper, SEO } from '../../components';
import { PageTypes } from '../../constants';
import { WorkshopPage } from '../../containers';
import Layout from '../../layout/default';
import { resolveEventMarketContext } from '../../utils/eventMarket';
import getServerSideTranslations from '../../utils/getServerSideTranslations';
import { mapContentfulToNextLocale } from '../../utils/localeMapper';
import { getMappedNavigation } from '../../utils/navigation';
import { eventType } from '../../utils/options';

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  if (!locales) {
    return {
      paths: [],
      fallback: 'blocking',
    };
  }

  const paths = [];

  if (CHANNEL && locales) {
    for (const locale of locales.filter((locale: string) => locale !== 'default')) {
      const events =
        eventType === 'TRAINING'
          ? await eventService.getTrainings(locale as string)
          : await eventService.getWorkshops(locale as string);

      for (const event of events) {
        paths.push({ locale: mapContentfulToNextLocale(locale), params: { slug: slugify(event.id) } });
      }
    }
  }

  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps = async ({ preview, locale, params }: GetStaticPropsContext) => {
  const slug = params?.slug as string | undefined;

  if (!locale || locale === 'default' || !slug) {
    return {
      revalidate: 60,
      notFound: true,
    };
  }

  const { contentfulLocaleMarket } = resolveEventMarketContext(locale);

  const [navigation, event] = await Promise.all([
    getMappedNavigation(contentfulLocaleMarket, !!preview),
    eventType === 'TRAINING'
      ? eventService.getTrainingById({ locale: contentfulLocaleMarket ?? '', eventId: slug as string })
      : eventService.getWorkshopById({ locale: contentfulLocaleMarket, eventId: slug as string }),
  ]);

  if (!event) {
    // ponytail: notFound must carry revalidate, else a regenerated 404 is pinned never-stale — transient fetch blips would brick the page until the next deploy
    return {
      revalidate: 60,
      notFound: true,
    };
  }

  const store = event?.location
    ? (await storeService.getStoreByStoreName(slugify(event?.location), locale)) ??
      (await storeService.getById(event?.locationId, locale))
    : null;

  return {
    // ponytail: ISR window chosen to match MongoDB enrollment propagation (~2-3 min per 46362) — bump to 300/3600 if BOSS API load ever matters
    revalidate: 60,
    props: {
      pageType: PageTypes.EVENT_DETAIL,
      store: store ?? null,
      slug,
      locale,
      preview: !!preview,
      navigation,
      environment: process.env.CONTENTFUL_ENVIRONMENT as Environment,
      event,
      ...(await getServerSideTranslations(locale, ['common', 'event', 'workshop', 'forms', 'routes'])),
    },
  };
};

export const Index = ({
  slug,
  navigation,
  locale,
  preview,
  environment,
  event,
  store,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const pageDescription = '';
  const pageTitle = event.title;
  const localizedSlugs = getTranslatedSlugsByType(slug, 'Event overview');
  const { t } = useTranslation('event');
  const crumbles: Crumble[] = [
    { href: getLocalizedSlugForEventPage(locale), label: t('pageTitle', { ns: 'event' }) },
    { href: getLocalizedSlugForEventPage(locale, slug), label: pageTitle },
  ];

  return (
    // TODO: add ogImage implementation
    <SEO
      breadcrumbs={crumbles}
      description={pageDescription}
      localizedSlugs={localizedSlugs}
      navigation={navigation}
      title={pageTitle}
    >
      <Layout
        crumbles={crumbles}
        environment={environment}
        localizedSlugs={localizedSlugs}
        navigation={navigation}
        preview={preview}
      >
        {event?.prefixSections?.map(entry => (
          <ComponentMapper entry={entry} key={entry.id} locale={locale} />
        ))}
        <WorkshopPage event={event} store={store} />
        {event?.suffixSections?.map(entry => (
          <ComponentMapper entry={entry} key={entry.id} locale={locale} />
        ))}
      </Layout>
    </SEO>
  );
};

export default Index;
